"use client";

import type { Dispatch, SetStateAction } from "react";
import type { useRouter } from "next/navigation";
import type { KundaliResponse } from "@/lib/api/kundali";
import { rememberLastReading } from "@/lib/last-reading";
import {
  trackKundaliFormSubmitFailed,
  trackKundaliGenerated,
  trackKundaliResultViewed,
} from "@/lib/analytics-kundali";
import type { KundaliLang } from "./KundaliFormCard.i18n";
import type { KundaliInterpretationQuality } from "@/lib/kundali-result-quality";
import type { KundaliFieldErrors } from "./KundaliFormCard.validation";
import { anchorResultSwapBeforeCommit } from "@/lib/result-swap-anchor";

export function currentPathname(): string | undefined {
  return typeof window !== "undefined" ? window.location.pathname || undefined : undefined;
}

function focusInvalidField(field: keyof KundaliFieldErrors) {
  window.setTimeout(() => {
    document.getElementById(field)?.focus();
    document.getElementById(field)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, 0);
}

export function handleInvalidKundaliForm({
  validation,
  setFieldErrors,
  setError,
  language,
  birthTime,
  analyticsSource,
  sourcePath,
}: {
  validation: { errors: KundaliFieldErrors; firstField: keyof KundaliFieldErrors; ok: false };
  setFieldErrors: Dispatch<SetStateAction<KundaliFieldErrors>>;
  setError: Dispatch<SetStateAction<string | null>>;
  language: KundaliLang;
  birthTime: string;
  analyticsSource: string;
  sourcePath: string | undefined;
}) {
  setFieldErrors(validation.errors);
  setError(validation.errors[validation.firstField] || "Please check the form.");
  focusInvalidField(validation.firstField);
  trackKundaliFormSubmitFailed({
    language,
    has_time: !!birthTime,
    source: analyticsSource,
    source_path: sourcePath,
    error_key: validation.firstField,
    error_field: validation.firstField,
    error_kind: "validation",
    error_message: validation.firstField,
    field_error_count: Object.keys(validation.errors).length,
  });
}

// Chat-first entries hand off to the dedicated /chat/<id> surface; result-first
// entries keep the inline result + chart. Add a page's KundaliFormCard `source`
// here to route it into the dedicated chat.
const CHAT_FIRST_SOURCES = new Set(["ai_astrologer", "free_astrology_chat", "nadi_astrology"]);

export function isChatFirstSource(source: string): boolean {
  return CHAT_FIRST_SOURCES.has(source);
}

/**
 * Post-generation handoff: persist + track, then either redirect a chat-first
 * entry to the dedicated /chat/<id> surface (returns true = navigated, so the
 * caller keeps the loading reveal through the transition) or swap the inline
 * result for result-first entries.
 */
export function finishKundali(
  response: KundaliResponse,
  ctx: {
    alreadyTracked?: boolean;
    source: string;
    language: KundaliLang;
    birthTime: string;
    analyticsSource: string;
    sourcePath: string | undefined;
  },
  router: ReturnType<typeof useRouter>,
  setResult: (response: KundaliResponse) => void,
  interpretationQuality: KundaliInterpretationQuality = "personalized"
): boolean {
  rememberLastReading({
    id: response.id,
    language: ctx.language,
    name: (response.birth_details as { name?: string } | undefined)?.name,
  });
  if (!ctx.alreadyTracked) trackSuccessfulKundali(response, ctx, interpretationQuality);
  if (isChatFirstSource(ctx.source)) {
    router.push(`/chat/${response.id}?lang=${ctx.language}`);
    return true;
  }
  // The real API commonly resolves 20-35s after submit, outside CLS's 500ms
  // input exclusion. Anchor BEFORE setResult mutates the DOM; the result's
  // layout effect is only a final alignment safeguard and cannot hide a shift
  // already attributed during the commit.
  anchorResultSwapBeforeCommit();
  setResult(response);
  return false;
}

export function trackSuccessfulKundali(
  response: KundaliResponse,
  ctx: {
    language: KundaliLang;
    birthTime: string;
    analyticsSource: string;
    sourcePath: string | undefined;
  },
  interpretationQuality: KundaliInterpretationQuality
) {
  trackKundaliGenerated({
    language: ctx.language,
    has_time: !!ctx.birthTime,
    source: ctx.analyticsSource,
    source_path: ctx.sourcePath,
  });
  trackKundaliResultViewed({
    reading_id: response.id,
    language: ctx.language,
    has_time: !!ctx.birthTime,
    source: ctx.analyticsSource,
    source_path: ctx.sourcePath,
    dosha_count: response.doshas.length,
    product_count: response.product_recommendations.length,
    interpretation_quality: interpretationQuality,
  });
}
